import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import TerserahApi from '../../helpers/TerserahApi';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 50px 14px 20px;
  font-size: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #25D366;
    box-shadow: 0 0 0 4px rgba(37, 211, 102, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #25D366 0%, #20BA5A 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  }
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionSection = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  padding: 8px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SuggestionItem = styled.div`
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  .icon {
    color: #999;
    font-size: 14px;
    width: 20px;
    text-align: center;
  }

  .text {
    flex: 1;
    color: #333;
  }

  .count {
    font-size: 12px;
    color: #999;
  }

  .remove {
    color: #999;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .remove {
    opacity: 1;
  }
`;

const SearchWithAutocomplete = ({ initialValue = '', placeholder, onSearch }) => {
  const api = TerserahApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [popularSearches, setPopularSearches] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    loadRecentSearches();
    loadPopularSearches();
  }, []);

  useEffect(() => {
    // Click outside to close dropdown
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRecentSearches = () => {
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(recent.slice(0, 5));
  };

  const loadPopularSearches = async () => {
    try {
      const json = await api.getPopularSearches(5);
      if (!json.error && json.searches) {
        setPopularSearches(json.searches);
      }
    } catch (error) {
      console.error('Error loading popular searches:', error);
    }
  };

  const saveToRecentSearches = (term) => {
    if (!term.trim()) return;

    let recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    // Remove if already exists
    recent = recent.filter(item => item.toLowerCase() !== term.toLowerCase());
    // Add to beginning
    recent.unshift(term);
    // Keep only last 10
    recent = recent.slice(0, 10);
    localStorage.setItem('recentSearches', JSON.stringify(recent));
    setRecentSearches(recent.slice(0, 5));
  };

  const removeRecentSearch = (term) => {
    let recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    recent = recent.filter(item => item !== term);
    localStorage.setItem('recentSearches', JSON.stringify(recent));
    setRecentSearches(recent.slice(0, 5));
  };

  const fetchSuggestions = async (query) => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const json = await api.getSearchSuggestions(query);
      if (!json.error && json.suggestions) {
        setSuggestions(json.suggestions);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Debounce suggestions
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSearch = (term = searchTerm) => {
    if (!term.trim()) return;

    saveToRecentSearches(term);
    setShowDropdown(false);

    if (onSearch) {
      onSearch(term);
    } else {
      navigate(`/ads?q=${encodeURIComponent(term)}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const term = typeof suggestion === 'string' ? suggestion : suggestion.term || suggestion.title;
    setSearchTerm(term);
    handleSearch(term);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const showSuggestionsDropdown = showDropdown && (
    suggestions.length > 0 || 
    recentSearches.length > 0 || 
    popularSearches.length > 0
  );

  return (
    <Container>
      <InputWrapper>
        <SearchInput
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('searchPlaceholder') || 'Buscar productos...'}
        />
        <SearchButton onClick={() => handleSearch()}>
          <i className="fas fa-search"></i>
        </SearchButton>
      </InputWrapper>

      {showSuggestionsDropdown && (
        <SuggestionsDropdown ref={dropdownRef}>
          {/* Suggestions from API */}
          {suggestions.length > 0 && (
            <SuggestionSection>
              <SectionTitle>{t('suggestions') || 'Sugerencias'}</SectionTitle>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem
                  key={`suggestion-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className="icon">
                    <i className="fas fa-search"></i>
                  </div>
                  <div className="text">
                    {typeof suggestion === 'string' ? suggestion : suggestion.term || suggestion.title}
                  </div>
                  {suggestion.count && (
                    <div className="count">{suggestion.count} {t('results')}</div>
                  )}
                </SuggestionItem>
              ))}
            </SuggestionSection>
          )}

          {/* Recent Searches */}
          {recentSearches.length > 0 && !searchTerm && (
            <SuggestionSection>
              <SectionTitle>{t('recentSearches') || 'Búsquedas recientes'}</SectionTitle>
              {recentSearches.map((term, index) => (
                <SuggestionItem
                  key={`recent-${index}`}
                  onClick={() => handleSuggestionClick(term)}
                >
                  <div className="icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="text">{term}</div>
                  <div
                    className="remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRecentSearch(term);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </div>
                </SuggestionItem>
              ))}
            </SuggestionSection>
          )}

          {/* Popular Searches */}
          {popularSearches.length > 0 && !searchTerm && (
            <SuggestionSection>
              <SectionTitle>{t('popularSearches') || 'Búsquedas populares'}</SectionTitle>
              {popularSearches.map((item, index) => (
                <SuggestionItem
                  key={`popular-${index}`}
                  onClick={() => handleSuggestionClick(item)}
                >
                  <div className="icon">
                    <i className="fas fa-fire"></i>
                  </div>
                  <div className="text">
                    {typeof item === 'string' ? item : item.term || item.query}
                  </div>
                  {item.count && (
                    <div className="count">{item.count}</div>
                  )}
                </SuggestionItem>
              ))}
            </SuggestionSection>
          )}
        </SuggestionsDropdown>
      )}
    </Container>
  );
};

export default SearchWithAutocomplete;
